import { ADMIN_USER_TYPE_KEY } from "@/core/constants";
import { getCookie } from "@/shared/utils/cookies";
import type { ParsedToken, User } from "firebase/auth";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { USER_TYPE } from "../types/user-role.types";

type AuthStoreState = {
  user: User | null;
  claims: ParsedToken | null;
  userType: string;
  isNavigator: boolean;
  orgId: string | null;
  isUser: boolean;
  isCreatorLite: boolean;
  isCreator: boolean;
  isAdministrator: boolean;
  isAdmin: boolean;
  isUserOrCreatorLite: boolean;
  isAdminOrAdministrator: boolean;
  isAdminCore: boolean;
  canCreateStories: boolean;
};

type AuthStoreActions = {
  setUser: (user: User | null) => void;
  setAuthClaims: (claims: ParsedToken | null) => void;
};

const initialState: AuthStoreState = {
  user: null,
  claims: null,
  userType: "user",
  isNavigator: false,
  orgId: null,
  isUser: false,
  isCreatorLite: false,
  isCreator: false,
  isAdministrator: false,
  isAdmin: false,
  isUserOrCreatorLite: false,
  isAdminOrAdministrator: false,
  isAdminCore: false,
  canCreateStories: false,
};

export const useAuthStore = create<AuthStoreState & AuthStoreActions>(
  (set) => ({
    ...initialState,
    setUser: (user) => set({ user }),
    setAuthClaims: (claims) => {
      set({ claims });
      const userType = getUserType(claims) as AuthStoreState["userType"];
      if (!claims) return set(initialState);
      set({
        userType: userType,
        isNavigator: claims.isNavigator as boolean,
        orgId: claims.orgId as string,
        isUser: userType === USER_TYPE.USER,
        isCreatorLite:
          userType === USER_TYPE.CREATOR_LITE ||
          userType === USER_TYPE.CREATOR_LITE_OLD,
        isCreator:
          userType === USER_TYPE.CREATOR || userType === USER_TYPE.ORG_ADMIN,
        isAdministrator: userType === USER_TYPE.ADMINISTRATOR,
        isAdmin: userType === USER_TYPE.STORYDOC_ADMIN,
        isAdminCore: !!claims?.admin,
        isUserOrCreatorLite:
          userType === USER_TYPE.USER ||
          userType === USER_TYPE.CREATOR_LITE ||
          userType === USER_TYPE.CREATOR_LITE_OLD,
        isAdminOrAdministrator:
          userType === USER_TYPE.STORYDOC_ADMIN ||
          userType === USER_TYPE.ADMINISTRATOR,
        canCreateStories:
          userType !== USER_TYPE.USER &&
          userType !== USER_TYPE.CREATOR_LITE &&
          userType !== USER_TYPE.CREATOR_LITE_OLD,
      });
    },
  }),
);

const getUserType = (claims: AuthStoreState["claims"]) => {
  if (claims?.admin) {
    const userType =
      getCookie(ADMIN_USER_TYPE_KEY) ||
      window.localStorage.getItem(ADMIN_USER_TYPE_KEY);
    return userType ? decodeURIComponent(userType) : USER_TYPE.ADMINISTRATOR;
  }
  return claims?.userType;
};

export const useAuthStoreShallow = <U>(
  selector: (store: AuthStoreState & AuthStoreActions) => U,
) => {
  return useAuthStore(useShallow(selector));
};
